import { Platform } from 'react-native'
import DeviceInfo from 'react-native-device-info'
import { trpc } from 'api'
import AppConfig from 'config'
import DateTime from '@ui/elements/DateTime'
import DateAgo from '@ui/elements/DateAgo'
import { useUpdates, reloadAsync } from 'expo-updates'
import VersionComponent from './VersionComponent'
import { appStatusVariant, AppVersionProps, cmsStatusVariant } from './types'
import { getAppsStatuses, getAllStatus, useCmsVersion } from './helpers'
import useRemoteConfigParams, {
  defaultRemoteConfigState as defaultCMS,
} from 'storage/useRemoteConfigParams'
import { useMemo } from 'react'
import { useRemoteConfigStore } from 'hooks/useRemoteConfigStore'
import { useIsNotFoundRoute } from 'hooks/useIsNotFoundRoute'

const isDev = AppConfig.isDev
const isWeb = Platform.OS === 'web'

export default function Version() {
  const isNotFoundRoute = useIsNotFoundRoute()
  const clientVersion = AppConfig.app.version
  const cms = useCmsVersion()
  const updates = useUpdates()
  const { toggle: toggleCmsVersion } = useRemoteConfigParams()
  const remoteConfigFetchTimestamp = useRemoteConfigStore(
    (state) => state.updatedAt
  )

  const { data: serverData, isLoading: serverLoading } = trpc.version.useQuery(
    {},
    {
      staleTime: Infinity,
    }
  )

  const { client, server } = useMemo(() => {
    const statuses = getAppsStatuses(clientVersion, serverData?.version)

    const client = {
      ...AppConfig.app,
      status: statuses.client,
    }

    const server = {
      ...serverData,
      status: statuses.server,
    }
    return { client, server }
  }, [clientVersion, serverData])

  const allStatus = getAllStatus({
    client: client.status,
    server: server.status,
    cms: cms.status,
  })

  const dateTimeLocale = 'de'
  const dateAgoLocale = 'en'
  const apps = useMemo(() => {
    const dateTimeOptions: Intl.DateTimeFormatOptions = {
      dateStyle: 'short',
      timeStyle: 'short',
    }

    const clientRows = [
      {
        label: 'Version',
        value: client.version,
      },

      !isWeb && {
        label: 'Native Version',
        value: DeviceInfo.getVersion(),
      },
      { label: 'Commit', value: client.hash },
      {
        label: 'Created',
        value: (
          <>
            <DateTime
              date={client.date}
              options={dateTimeOptions}
              locale={dateTimeLocale}
            />
            {', '}
            <DateAgo date={client.date} locale={dateAgoLocale} />
          </>
        ),
      },
    ].filter((v) => !!v)

    return [
      {
        title: Platform.select({
          default: 'App',
          ios: 'iPad',
          web: 'Web',
        }),
        actionClose: true,
        action: updates.isUpdatePending
          ? {
              name: 'reload',
              onPress: reloadAsync,
              size: 20,
            }
          : undefined,
        loading: client.status === 'unknown',
        status: {
          text: client.status,
          variant: appStatusVariant[client.status],
        },
        rows: clientRows,
      },
      {
        title: 'Server',
        loading: serverLoading,
        status: {
          text: server.status,
          variant: appStatusVariant[server.status],
        },
        rows: [
          { label: 'Version', value: server?.version },
          { label: 'Commit', value: server?.hash },
          {
            label: 'Created',
            value: (
              <>
                <DateTime
                  date={server?.date}
                  options={dateTimeOptions}
                  locale={dateTimeLocale}
                />
                {', '}
                <DateAgo date={server?.date} locale={dateAgoLocale} />
              </>
            ),
          },
        ],
      },
      {
        title: 'CMS',
        loading: cms.isLoading,
        status: {
          text: cms.status,
          variant: cmsStatusVariant[cms.status],
        },
        action:
          isDev || cms.status === 'preview'
            ? {
                name:
                  cms.status === defaultCMS
                    ? 'circle-check-filled'
                    : 'circle-check',
                onPress: toggleCmsVersion,
                size: 20,
              }
            : undefined,
        rows: [
          {
            label: 'Version',
            value: cms?.result?.description,
          },
          {
            label: 'Published',
            value: (
              <>
                <DateTime
                  date={cms?.result?.publishedAt}
                  options={dateTimeOptions}
                  locale={dateTimeLocale}
                />
                {', '}
                <DateAgo
                  date={cms?.result?.publishedAt}
                  locale={dateAgoLocale}
                />
              </>
            ),
          },
          {
            label: 'Fetched',
            value: (
              <>
                <DateTime
                  date={new Date(remoteConfigFetchTimestamp)}
                  options={dateTimeOptions}
                  locale={dateTimeLocale}
                />
                {', '}
                <DateAgo
                  date={new Date(remoteConfigFetchTimestamp)}
                  locale={dateAgoLocale}
                />
              </>
            ),
          },
        ],
      },
    ] as const satisfies AppVersionProps[]
  }, [
    client,
    cms,
    server,
    toggleCmsVersion,
    serverLoading,
    updates.isUpdatePending,
    remoteConfigFetchTimestamp,
  ])

  if (isNotFoundRoute) {
    return null
  }

  return (
    <VersionComponent version={client.version} status={allStatus} apps={apps} />
  )
}
